import {
  Box,
  Flex,
  Icon,
  SimpleGrid,
  useColorModeValue,
  Spinner,
  Text,
  Center,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import MiniStatistics from "components/card/MiniStatistics";
import IconBox from "components/icons/IconBox";
import React, { useEffect, useState } from "react";
import {
  MdRemoveRedEye,
  MdAddTask,
  MdVpnKey,
  MdApi
} from "react-icons/md";
import {
  FaUsers,
  FaSync
} from "react-icons/fa";
import TotalSpent from "views/admin/default/components/TotalSpent";
import Card from "components/card/Card.js";
import UserAvatar from "components/UserAvatar";

import config from "config";
import { useUser } from "contexts/UserContext";

export default function UserReports() {
  const brandColor = useColorModeValue("brand.500", "white");
  const boxBg = useColorModeValue("secondaryGray.300", "navy.700");
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");

  const { user, loading: userLoading } = useUser();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [overview, setOverview] = useState({});
  const [apiRequests, setApiRequests] = useState([]);
  const [previousRequests, setPreviousRequests] = useState([]);
  const [chartLabels, setChartLabels] = useState([]);
  const [features, setFeatures] = useState([]);

  async function getDashboard() {
    const token = localStorage.getItem("token");
    const response = await fetch(`${config.BACKEND_API_URL}/auth/dashboard`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      }
    });

    return await response.json();
  }

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const dashboardresponse = await getDashboard();
        const data = dashboardresponse.data;

        const featuresData = [
          { name: "Active APIs", status: "working", count: data?.stats?.activeServices ?? 0 },
          { name: "Unstable APIs", status: "error", count: data?.stats?.unstableServices ?? 0 },
          { name: "Down APIs", status: "disabled", count: data?.stats?.downServices ?? 0 }
        ];

        setOverview({
          views: data?.stats?.totalVisitors ?? 0,
          users: data?.stats?.totalUsers ?? 0,
          requests: data?.stats?.totalRequests ?? 0,
          apiKey: data?.user?.apiKey || 'N/A',
          activeRequests: data?.stats?.activeServices ?? 0,
          totalEndpoints: data?.stats?.totalEndpoints ?? 0
        });

        setApiRequests(data.chart?.data || []);
        setPreviousRequests(data.chart?.previous || []);
        setChartLabels(data.chart?.labels || []);
        setFeatures(featuresData);

      } catch (err) {
        setError(err.message || "Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading || userLoading) {
    return (
      <Center pt={{ base: "130px", md: "80px", xl: "80px" }} w="100%" h="100vh">
        <Spinner size="xl" color={brandColor} />
      </Center>
    );
  }

  if (error) {
    return (
      <Center pt={{ base: "130px", md: "80px", xl: "80px" }} w="100%" h="100vh">
        <Text color="red.500" fontSize="xl">{error}</Text>
      </Center>
    );
  }

  const getStatusColor = (status) => {
    if (status === "working") return "green.500";
    if (status === "disabled") return "red.500";
    if (status === "error") return "yellow.500";
    return "gray.500";
  };

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <Card mb="20px" p="20px" flexDirection="row" alignItems="center">
        <UserAvatar size="md" w="48px" h="48px" me="15px" />
        <Box>
          <Text fontWeight="700" color={textColor} fontSize="lg">
            Welcome back, {user?.name || "User"}!
          </Text>
          <Text color="secondaryGray.600" fontSize="sm">
            Current Plan: <Text as="span" fontWeight="700" textTransform="capitalize">{user?.plan || "Free"}</Text>
          </Text>
        </Box>
      </Card>

      <SimpleGrid columns={{ base: 1, md: 2, lg: 3, "2xl": 5 }} gap='20px' mb='20px'>
        <MiniStatistics
          startContent={<IconBox w='56px' h='56px' bg={boxBg} icon={<Icon w='32px' h='32px' as={MdRemoveRedEye} color={brandColor} />} />}
          name='Total Visitors'
          value={overview.views?.toLocaleString()}
        />
        <MiniStatistics
          startContent={<IconBox w='56px' h='56px' bg={boxBg} icon={<Icon w='32px' h='32px' as={FaUsers} color={brandColor} />} />}
          name='Total Users'
          value={overview.users?.toLocaleString()}
        />
        <MiniStatistics
          startContent={<IconBox w='56px' h='56px' bg={boxBg} icon={<Icon w='32px' h='32px' as={FaSync} color={brandColor} />} />}
          name='Total Requests'
          value={overview.requests?.toLocaleString()}
        />
        <MiniStatistics
          startContent={<IconBox w='56px' h='56px' bg={boxBg} icon={<Icon w='32px' h='32px' as={MdVpnKey} color={brandColor} />} />}
          name='API Key'
          value={overview.apiKey || 'N/A'}
        />
        <MiniStatistics
          startContent={<IconBox w='56px' h='56px' bg='linear-gradient(90deg, #4481EB 0%, #04BEFE 100%)' icon={<Icon w='28px' h='28px' as={MdAddTask} color='white' />} />}
          name='Active Services'
          value={overview.activeRequests?.toString()}
        />
        <MiniStatistics
          startContent={ <IconBox w='56px' h='56px' bg={boxBg} icon={<Icon w='32px' h='32px' as={MdApi} color={brandColor} />} />}
          name='Total APIs'
          value={overview.totalEndpoints?.toString()}
        />
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, md: 1, xl: 2 }} gap='20px' mb='20px'>
        <TotalSpent
          revenueData={apiRequests}
          previousData={previousRequests}
          labels={chartLabels}
          totalRevenue={apiRequests.length > 0 ? apiRequests.reduce((a, b) => a + b, 0) : 0}
          title="API Requests (Last 7 Days)"
          isDollar={false}
        />

        <Card flexDirection="column" w="100%" px="0px" overflowX={{ sm: "scroll", lg: "hidden" }}>
          <Flex px="25px" mb="8px" justifyContent="space-between" align="center">
            <Text color={textColor} fontSize="22px" fontWeight="700">API Services Status</Text>
          </Flex>
          <Box>
            <Table variant="simple" color="gray.500" mb="24px" mt="12px">
              <Thead>
                <Tr>
                  <Th borderColor={borderColor}><Text fontSize="12px">SERVICE</Text></Th>
                  <Th borderColor={borderColor}><Text fontSize="12px">STATUS</Text></Th>
                  <Th borderColor={borderColor}><Text fontSize="12px">COUNT</Text></Th>
                </Tr>
              </Thead>
              <Tbody>
                {features.map((feature, index) => (
                  <Tr key={index}>
                    <Td borderColor="transparent">
                      <Text color={textColor} fontSize="sm" fontWeight="700">
                        {feature.name}
                      </Text>
                    </Td>
                    <Td borderColor="transparent">
                      <Flex align="center">
                        <Box w="8px" h="8px" borderRadius="50%" bg={getStatusColor(feature.status)} me="8px" />
                        <Text color={getStatusColor(feature.status)} fontSize="sm" fontWeight="700">
                          {feature.status}
                        </Text>
                      </Flex>
                    </Td>
                    <Td borderColor="transparent">
                      <Text color={textColor} fontSize="sm" fontWeight="700">
                        {feature.count}
                      </Text>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </Card>
      </SimpleGrid>
    </Box>
  );
}
